import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { Calendar } from '../../../../../shared/components/widgets/calendar/calendar.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import type { CustomPriceModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import {
  adjustCustomPrices,
  PriceAdjustmentChangeType,
  PriceAdjustmentDirection,
  type AdjustmentPeriod,
  type PriceAdjustmentPreviewRow
} from '../../../../shared/services/custom-price-adjustment.service';

export interface AdjustCustomPriceModalProps {
  data: {
    customPrices: CustomPriceModel[];
    /** The unit's base nightly price in USD, used for days with no dynamic price yet. */
    basePriceInUSD: number;
    /** The unit's base nightly price in the display currency, shown on the calendar tiles. */
    basePrice: number;
  };
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    closeHandler: () => void;
    applyAdjustment: (customPrices: CustomPriceModel[]) => void;
  };
}

const MAX_PERCENTAGE = 500;
const PREVIEW_ROW_LIMIT = 60;

const CHANGE_TYPE_BADGES: Record<PriceAdjustmentChangeType, { label: string; className: string }> =
  {
    [PriceAdjustmentChangeType.adjusted]: {
      label: 'Adjusted',
      className: 'bg-primary-50 text-primary-500'
    },
    [PriceAdjustmentChangeType.created]: {
      label: 'New',
      className: 'bg-green-50 text-green-700'
    },
    [PriceAdjustmentChangeType.unchanged]: {
      label: 'Unchanged',
      className: 'bg-gray-100 text-gray-500'
    }
  };

const formatDay = (epoch: number) => new Date(epoch).toDateString();

/**
 * Turns the flat list of dates the calendar emits into [from, to] periods.
 * A trailing unpaired date is a half-finished selection and is ignored.
 */
const toPeriods = (dates: Date[]): AdjustmentPeriod[] => {
  const periods: AdjustmentPeriod[] = [];
  for (let index = 0; index + 1 < dates.length; index += 2) {
    periods.push({ from: dates[index], to: dates[index + 1] });
  }
  return periods;
};

/**
 * Lets the ecommerce team raise or lower a unit's dynamic prices by a percentage over
 * the periods they pick, respecting the ranges already in the customPrices array.
 */
export const AdjustCustomPriceModal: React.FC<AdjustCustomPriceModalProps> = ({
  data,
  config,
  eventHandlers
}) => {
  const isMobileView = useWindowResizer();
  const [direction, setDirection] = useState<PriceAdjustmentDirection>(
    PriceAdjustmentDirection.increase
  );
  const [percentage, setPercentage] = useState<string>('');
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);

  useEffect(() => {
    if (config.showModal) {
      setDirection(PriceAdjustmentDirection.increase);
      setPercentage('');
      setSelectedDates([]);
      setIsConfirming(false);
    }
  }, [config.showModal]);

  const periods = useMemo(() => toPeriods(selectedDates), [selectedDates]);
  const parsedPercentage = Number(percentage);
  const isPercentageValid =
    percentage !== '' &&
    Number.isFinite(parsedPercentage) &&
    parsedPercentage > 0 &&
    parsedPercentage <= MAX_PERCENTAGE &&
    (direction === PriceAdjustmentDirection.increase || parsedPercentage <= 100);
  const canPreview = isPercentageValid && periods.length > 0;

  const result = useMemo(() => {
    if (!canPreview) return undefined;
    return adjustCustomPrices({
      customPrices: data.customPrices || [],
      periods,
      percentage: parsedPercentage,
      direction,
      basePriceInUSD: data.basePriceInUSD
    });
  }, [canPreview, data.customPrices, data.basePriceInUSD, periods, parsedPercentage, direction]);

  const changedRows = useMemo(
    () =>
      (result?.rows || []).filter(
        (row) => row.changeType !== PriceAdjustmentChangeType.unchanged
      ),
    [result]
  );

  const hasChanges = changedRows.length > 0;
  const missingBasePrice = !(data.basePriceInUSD > 0);

  const percentageError = (() => {
    if (percentage === '' || isPercentageValid) return undefined;
    if (!Number.isFinite(parsedPercentage) || parsedPercentage <= 0) {
      return 'Enter a percentage greater than 0.';
    }
    if (direction === PriceAdjustmentDirection.decrease && parsedPercentage > 100) {
      return "A decrease can't be more than 100%.";
    }
    return `Enter a percentage up to ${MAX_PERCENTAGE}%.`;
  })();

  const handleApply = () => {
    if (!result || !hasChanges) return;
    eventHandlers.applyAdjustment(result.customPrices);
    eventHandlers.closeHandler();
  };

  const renderRow = (row: PriceAdjustmentPreviewRow, index: number) => {
    const badge = CHANGE_TYPE_BADGES[row.changeType];
    return (
      <div
        key={`${row.from}-${row.to}-${index}`}
        className="flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 last:border-b-0"
      >
        <div className="min-w-0">
          <div className="text-[1.4rem] font-gilroy-medium text-gray-900 truncate">
            {formatDay(row.from)} &rarr; {formatDay(row.to)}
          </div>
          <div className="text-[1.2rem] text-gray-500">
            {row.numberOfDays} night{row.numberOfDays === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-[1.4rem] font-gilroy-medium text-gray-900 whitespace-nowrap">
            {row.changeType === PriceAdjustmentChangeType.created ? (
              <>
                <span className="text-gray-400">base ${row.previousPriceInUSD}</span>
                <span className="mx-1.5 text-gray-400">&rarr;</span>
              </>
            ) : (
              <>
                <span className="text-gray-400 line-through">${row.previousPriceInUSD}</span>
                <span className="mx-1.5 text-gray-400">&rarr;</span>
              </>
            )}
            <span className="font-gilroy-bold">${row.priceInUSD}</span>
          </div>
          <span
            className={`rounded px-2 py-0.5 text-[1.1rem] font-gilroy-medium uppercase tracking-[0.05em] ${badge.className}`}
          >
            {badge.label}
          </span>
        </div>
      </div>
    );
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true, maxWidth: 'md' }}
      eventHandlers={{ closeModalHandler: eventHandlers.closeHandler }}
    >
      <div className="w-full p-4 md:p-8 font-gilroy-regular">
        <div className="mb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="text-[1.9rem] leading-tight font-gilroy-bold text-gray-900">
            Adjust Prices by Percentage
          </div>
          <div className="inline-flex items-center rounded-md border border-action-alert bg-action-alert bg-opacity-10 px-2 py-1 text-[1.2rem] text-neutral-800">
            <span className="mr-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-action-alert text-white text-[1rem] font-gilroy-bold">
              !
            </span>
            Applies to this unit only
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-white px-4 py-3 mb-4 text-[1.3rem] leading-normal text-gray-600">
          Pick one or more periods on the calendar, then set the percentage. Dynamic prices are
          adjusted only for the days inside those periods &mdash; a range that reaches outside is
          split so the days outside keep their price. Days with no dynamic price get one derived
          from the unit&apos;s base price of{' '}
          <span className="font-gilroy-bold text-gray-900">${data.basePriceInUSD || 0}</span>.
        </div>

        {missingBasePrice && (
          <div className="rounded-lg border border-action-alert bg-action-alert bg-opacity-10 px-4 py-2 mb-4 text-[1.3rem] text-neutral-800">
            This unit has no base price in USD, so days with no dynamic price will be left
            uncovered.
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="md:w-1/2">
            <span className="block text-[1.2rem] font-gilroy-medium uppercase tracking-[0.07em] text-gray-500 mb-2">
              Change
            </span>
            <div className="flex gap-2">
              <Button
                onClick={() => setDirection(PriceAdjustmentDirection.increase)}
                type={
                  direction === PriceAdjustmentDirection.increase
                    ? ButtonType.primary
                    : ButtonType.primaryInverted
                }
                size={ButtonSize.medium}
              >
                Increase
              </Button>
              <Button
                onClick={() => setDirection(PriceAdjustmentDirection.decrease)}
                type={
                  direction === PriceAdjustmentDirection.decrease
                    ? ButtonType.primary
                    : ButtonType.primaryInverted
                }
                size={ButtonSize.medium}
              >
                Decrease
              </Button>
            </div>
          </div>

          <div className="md:w-1/2">
            <span className="block text-[1.2rem] font-gilroy-medium uppercase tracking-[0.07em] text-gray-500 mb-2">
              Percentage
            </span>
            <InputText
              data={{
                value: percentage,
                placeholder: 'e.g. 20'
              }}
              config={{
                maxRows: 1,
                autoFocus: true,
                type: InputTextType.number,
                error: !!percentageError
              }}
              eventHandlers={{
                onChangeHandler: (value: string) => setPercentage(value)
              }}
            />
            {percentageError && (
              <div className="mt-1.5 text-[1.2rem] text-action-alert">{percentageError}</div>
            )}
          </div>
        </div>

        <div className="mb-4">
          <span className="block text-[1.2rem] font-gilroy-medium uppercase tracking-[0.07em] text-gray-500 mb-2">
            Periods
          </span>
          <Calendar
            data={{
              calenderView: true,
              showDateInput: false,
              basePriceTag: data.basePrice,
              customPriceTags: data.customPrices,
              multipleRanges: true
            }}
            eventHandlers={{
              onChange: (dates) => {
                setSelectedDates(Array.isArray(dates) ? (dates as Date[]) : []);
                setIsConfirming(false);
              }
            }}
          />
          <div className="mt-1.5 text-[1.2rem] text-gray-500">
            {periods.length === 0
              ? 'Select a start and an end date to add a period. Click a selected period again to remove it.'
              : `${periods.length} period${periods.length === 1 ? '' : 's'} selected.`}
          </div>
        </div>

        {result && (
          <div className="border border-gray-200 rounded-xl mb-6 overflow-hidden">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
              <div>
                <div className="text-[1.5rem] font-gilroy-bold text-gray-900">Preview</div>
                <div className="text-[1.2rem] text-gray-500">
                  {result.summary.adjusted} adjusted, {result.summary.created} new,{' '}
                  {result.summary.unchanged} unchanged
                </div>
              </div>
              <div className="text-[1.3rem] font-gilroy-medium text-gray-600">
                {direction === PriceAdjustmentDirection.increase ? '+' : '-'}
                {parsedPercentage}% over {periods.length} period
                {periods.length === 1 ? '' : 's'}
              </div>
            </div>

            {hasChanges ? (
              <div className="max-h-[240px] overflow-y-auto">
                {changedRows.slice(0, PREVIEW_ROW_LIMIT).map(renderRow)}
                {changedRows.length > PREVIEW_ROW_LIMIT && (
                  <div className="px-4 py-2 text-[1.2rem] text-gray-500">
                    and {changedRows.length - PREVIEW_ROW_LIMIT} more change
                    {changedRows.length - PREVIEW_ROW_LIMIT === 1 ? '' : 's'}&hellip;
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 text-[1.3rem]">
                Nothing to change for the selected periods.
              </div>
            )}
          </div>
        )}

        <div className="pt-4 border-t border-gray-200">
          {isConfirming && (
            <div className="mb-3 text-[1.4rem] leading-normal text-gray-900">
              This will {direction === PriceAdjustmentDirection.increase ? 'increase' : 'decrease'}{' '}
              prices by {parsedPercentage}% across{' '}
              {changedRows.reduce((total, row) => total + row.numberOfDays, 0)} night(s). Apply?
            </div>
          )}
          <div className="flex flex-col justify-end md:flex-row gap-3">
            <Button
              onClick={() => (isConfirming ? setIsConfirming(false) : eventHandlers.closeHandler())}
              type={ButtonType.primaryInverted}
              size={isMobileView ? ButtonSize.full : ButtonSize.medium}
            >
              {isConfirming ? 'Back' : 'Cancel'}
            </Button>
            <Button
              onClick={() => (isConfirming ? handleApply() : setIsConfirming(true))}
              size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              disabled={!hasChanges}
              {...(isConfirming && {
                tracking: {
                  event: 'record_saved' as const,
                  entity: 'custom_price' as const,
                  action: 'update' as const,
                  outcome: 'success' as const
                }
              })}
            >
              {isConfirming ? 'Yes, apply' : 'Apply Adjustment'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AdjustCustomPriceModal;
